import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import './landing.css';

const StarSvg = () => (
  <svg viewBox="0 0 24 24">
    <path d="M12 17.3 5.8 21l1.6-7.1L2 9.2l7.3-.6L12 2l2.7 6.6 7.3.6-5.4 4.7L18.2 21z" />
  </svg>
);

const CheckSvg = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="4 12 10 18 20 6" />
  </svg>
);

const AppStoreButton = ({ gpId = 'gp' }) => (
  <a href="#" className="store-btn" aria-label="Download on the App Store">
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M17.05 12.536c-.02-2.32 1.9-3.44 1.99-3.49-1.09-1.59-2.78-1.81-3.38-1.83-1.44-.15-2.81.85-3.54.85-.74 0-1.86-.83-3.06-.81-1.57.02-3.02.91-3.83 2.32-1.64 2.84-.42 7.05 1.18 9.35.78 1.13 1.71 2.39 2.94 2.34 1.18-.05 1.63-.76 3.06-.76 1.43 0 1.83.76 3.08.74 1.27-.02 2.08-1.14 2.86-2.27.9-1.3 1.27-2.56 1.29-2.62-.03-.01-2.47-.95-2.49-3.76zM14.7 5.3c.65-.79 1.09-1.88.97-2.97-.94.04-2.08.62-2.75 1.4-.6.69-1.13 1.8-.99 2.87 1.05.08 2.12-.53 2.77-1.3z" />
    </svg>
    <span className="lines">
      <span className="top">Download on the</span>
      <span className="bot">App Store</span>
    </span>
  </a>
);

const GooglePlayButton = ({ gpId = 'gp' }) => (
  <a href="#" className="store-btn" aria-label="Get it on Google Play">
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path fill={`url(#${gpId}1)`} d="M13.5 12 3.6 3.5c-.4.2-.6.7-.6 1.3v14.4c0 .6.2 1.1.6 1.3L13.5 12z" />
      <path fill={`url(#${gpId}2)`} d="m17.3 15.3-3.2-3.3 3.2-3.3 3.9 2.2c1.1.6 1.1 1.6 0 2.2l-3.9 2.2z" />
      <path fill={`url(#${gpId}3)`} d="M3.6 20.5c.4.3 1 .4 1.6 0l12.1-6.9-3.8-3.8L3.6 20.5z" />
      <path fill={`url(#${gpId}4)`} d="m3.6 3.5 9.9 9.9 3.8-3.8L5.2 3.6c-.3-.2-.6-.2-.9-.2-.3 0-.5.1-.7.1z" />
      <defs>
        <linearGradient id={`${gpId}1`} x1="6.8" y1="3.6" x2="6.8" y2="20.4" gradientUnits="userSpaceOnUse"><stop stopColor="#00A1FF" /><stop offset="1" stopColor="#00D4FF" /></linearGradient>
        <linearGradient id={`${gpId}2`} x1="21.2" y1="12" x2="13.3" y2="12" gradientUnits="userSpaceOnUse"><stop stopColor="#FFE000" /><stop offset="1" stopColor="#FFBD00" /></linearGradient>
        <linearGradient id={`${gpId}3`} x1="15.4" y1="14.2" x2="3.3" y2="21.2" gradientUnits="userSpaceOnUse"><stop stopColor="#FF3A44" /><stop offset="1" stopColor="#C31162" /></linearGradient>
        <linearGradient id={`${gpId}4`} x1="3.3" y1="2.8" x2="15.4" y2="9.8" gradientUnits="userSpaceOnUse"><stop stopColor="#32A071" /><stop offset="1" stopColor="#2DA771" /></linearGradient>
      </defs>
    </svg>
    <span className="lines">
      <span className="top">Get it on</span>
      <span className="bot">Google Play</span>
    </span>
  </a>
);

const faqItems = [
  {
    q: 'How is this different from just keeping a skincare diary?',
    a: "A diary stores words. Glowlytix stores structured, queryable data. You log your skin using the same 8 fields every day, which means the AI can actually correlate your score against your sleep, your cycle, your products, your diet. A notes app can't do that.",
  },
  {
    q: 'What about my data and privacy?',
    a: "Your photos and logs are stored securely on our servers, encrypted in transit via HTTPS. We do not sell your data or share it with advertisers. AI insights are generated via OpenAI's API using your anonymised log data. You can export your full data or permanently delete your account at any time from Settings.",
  },
  {
    q: 'How long until I see meaningful insights?',
    a: 'Most users start seeing reliable patterns around day 21. The AI needs enough data points to distinguish signal from noise. The timelapse becomes genuinely compelling around week 6.',
  },
  {
    q: 'Is this a medical app?',
    a: "No — Glowlytix is a wellness tool, not a medical device. We don't diagnose conditions, prescribe treatments, or replace a dermatologist. We give you the data; you (and your derm) make the call.",
  },
  {
    q: 'Can I use it alongside my dermatologist?',
    a: "That's exactly the point. Walk into your appointment with a 12-week timelapse, your glow trend, your routine log, and your triggers. Your derm will love you. Actually, they won't — but they'll give you better advice.",
  },
  {
    q: 'What if I miss a day? Or a week?',
    a: "Completely fine. The insights engine is built for imperfect, real-human data. A missed week won't break your streak in any meaningful way — we'd rather have honest gaps than fabricated entries.",
  },
];

export default function LandingPage() {
  const rootRef = useRef(null);
  const [scrolled, setScrolled] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    // Remove the default app centering styles while on the landing page
    const prevBodyBg = document.body.style.background;
    const prevRootDisplay = document.documentElement.style.background;
    document.body.style.background = 'transparent';
    document.title = 'Glowlytix — Your skin has a story. Start tracking it.';
    return () => {
      document.body.style.background = prevBodyBg;
      document.documentElement.style.background = prevRootDisplay;
    };
  }, []);

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    root.classList.add('js-ready');
    const reveals = root.querySelectorAll('.reveal');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e, i) => {
        if (e.isIntersecting) {
          e.target.style.transitionDelay = (i * 60) + 'ms';
          e.target.classList.add('in');
          io.unobserve(e.target);
        }
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });
    reveals.forEach((el) => io.observe(el));

    const vh = window.innerHeight;
    reveals.forEach((el, i) => {
      const r = el.getBoundingClientRect();
      if (r.top < vh && r.bottom > 0) {
        el.style.transitionDelay = Math.min(i, 8) * 60 + 'ms';
        el.classList.add('in');
      }
    });

    const safety = setTimeout(() => {
      reveals.forEach((el) => el.classList.add('in'));
    }, 1200);

    return () => {
      io.disconnect();
      clearTimeout(safety);
    };
  }, []);

  return (
    <div ref={rootRef} className="gl-landing" data-font="sans">
      {/* NAV */}
      <nav className={`nav${scrolled ? ' scrolled' : ''}`} id="nav">
        <div className="container nav-inner">
          <a href="#" className="brand" aria-label="Glowlytix home">
            <img className="brand-mark" src="/glowlytix-logo.png" alt="" />
            <span className="brand-word">Glowlytix</span>
          </a>
          <div className="nav-links">
            <a href="#features">Features</a>
            <a href="#how">How it works</a>
            <a href="#pricing">Pricing</a>
            <a href="#faq">FAQ</a>
          </div>
          <Link to="/app" className="btn btn-primary nav-cta">Download free</Link>
        </div>
      </nav>

      {/* HERO */}
      <header className="hero">
        <div className="blob blob-1" />
        <div className="blob blob-2" />
        <div className="blob blob-3" />
        <div className="container hero-grid">
          <div className="hero-left">
            <span className="badge reveal"><span className="dot" /> Now on iOS &amp; Android</span>
            <h1 className="reveal">Your skin has a <span className="display-italic gradient-text">story</span>. Start tracking it.</h1>
            <p className="sub reveal">The AI skin journal for women who are done guessing. Log daily, see what's actually working, and finally connect the dots between your routine and your glow.</p>
            <div className="cta-row reveal" id="download">
              <AppStoreButton />
              <GooglePlayButton gpId="gpA" />
            </div>
            <div className="social-proof reveal">
              <div className="avatar-stack" aria-hidden="true"><span /><span /><span /><span /></div>
              <div className="stars" aria-label="5 out of 5 stars">
                <StarSvg /><StarSvg /><StarSvg /><StarSvg /><StarSvg />
              </div>
              <span><b style={{ color: 'var(--ink)', fontWeight: 500 }}>Early Access</b> &middot; Beta users</span>
            </div>
          </div>

          <div className="hero-right">
            <div className="phone-wrap reveal">
              <div className="phone">
                <div className="screen">
                  <div className="notch" />
                  <div className="status">
                    <span>9:41</span>
                    <span>●●●● ◦ 􀙇</span>
                  </div>
                  <div className="app-head">
                    <div className="hi"><small>Good morning</small>Hello, Juliana</div>
                    <div className="ava" />
                  </div>
                  <div className="score-card">
                    <div className="score-top">
                      <div>
                        <div className="score-label">Today's glow score</div>
                        <div className="score-num">8.4<span className="slash">/10</span></div>
                      </div>
                      <span className="score-delta">
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor"><path d="M5 2 9 7 H1 z" /></svg>
                        +12% this week
                      </span>
                    </div>
                    <div className="score-bar"><i /></div>
                  </div>
                  <div className="tags">
                    <span className="tag pink">Hydrated</span>
                    <span className="tag mint">Clear</span>
                    <span className="tag lilac">Even tone</span>
                    <span className="tag">Smooth</span>
                  </div>
                  <div className="chart-card">
                    <div className="chart-head">
                      <span className="chart-title">7-day glow trend</span>
                      <span className="chart-pill">Week view</span>
                    </div>
                    <div className="bars"><b /><b /><b /><b /><b /><b /><b /></div>
                    <div className="bar-days"><span>M</span><span>T</span><span>W</span><span>T</span><span>F</span><span>S</span><span>S</span></div>
                  </div>
                </div>
              </div>

              <div className="float-card f-top">
                <div className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="22 7 13.5 15.5 8.5 10.5 2 17" />
                    <polyline points="16 7 22 7 22 13" />
                  </svg>
                </div>
                <div>
                  <div className="meta">7-day trend</div>
                  <div className="val">Glow up<b>+12%</b></div>
                </div>
              </div>
              <div className="float-card f-bot">
                <div className="ic">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <circle cx="12" cy="12" r="10" />
                    <path d="M12 6v6l4 2" />
                  </svg>
                </div>
                <div>
                  <div className="meta">Top glow booster</div>
                  <div className="val">8hr sleep</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* MARQUEE */}
      <div className="marquee" aria-hidden="true">
        <div className="marquee-track">
          <span>Daily skin logging</span><span>AI-powered insights</span><span>Photo timeline tracking</span><span>Glow score analytics</span><span>Routine correlation</span><span>Cycle phase tracking</span><span>Timelapse generation</span><span>Supplement logging</span>
          <span>Daily skin logging</span><span>AI-powered insights</span><span>Photo timeline tracking</span><span>Glow score analytics</span><span>Routine correlation</span><span>Cycle phase tracking</span><span>Timelapse generation</span><span>Supplement logging</span>
        </div>
      </div>

      {/* PROBLEM */}
      <section className="block problem">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">Sound familiar?</span>
            <h2>You've tried <span className="display-italic gradient-text">everything</span>. You remember none of it.</h2>
            <p>You've got a shelf of half-used serums, a camera roll of mystery bathroom selfies, and no idea what was working that one month your skin looked incredible. This isn't a skincare problem. It's a data problem.</p>
          </div>
          <div className="pain-grid">
            <div className="pain-card reveal">
              <div className="pain-card-img" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1556228720-195a672e8a03?w=800&q=80')" }} />
              <div className="pain-card-body">
                <div className="pain-num">No. 01</div>
                <h3>The endless product swap</h3>
                <p>You pick up a new serum, use it for three weeks, decide it's "meh," and move on. Most actives need 6–8 weeks to show up. You'll never know what actually worked.</p>
              </div>
            </div>
            <div className="pain-card reveal">
              <div className="pain-card-img" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1526045478516-99145907023c?w=800&q=80')" }} />
              <div className="pain-card-body">
                <div className="pain-num">No. 02</div>
                <h3>Mystery breakouts</h3>
                <p>Was it the new moisturizer? The sushi? Your period? Stress? Without a record of what you ate, wore, and did, you can't connect a single dot.</p>
              </div>
            </div>
            <div className="pain-card reveal">
              <div className="pain-card-img" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1512290923902-8a9f81dc236c?w=800&q=80')" }} />
              <div className="pain-card-body">
                <div className="pain-num">No. 03</div>
                <h3>No real progress photos</h3>
                <p>Your camera roll has 400 bathroom mirror selfies in bad lighting, zero consistency, zero context. Useless for comparing week one to week twelve.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="block features" id="features">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">What's inside</span>
            <h2>Finally, a record that <span className="display-italic gradient-text">keeps up</span> with your skin.</h2>
            <p>Seven tools designed to turn your skincare from a vibe into a body of evidence. Log it once; let the app do the pattern matching.</p>
          </div>
          <div className="feat-grid">
            <div className="feat-card span-3 reveal">
              <div className="feat-label">Daily log</div>
              <h3>8 fields. 30 seconds. A complete snapshot.</h3>
              <p>Hydration, sleep, stress, diet, cycle, routine, mood, environment. Eight taps and you're done.</p>
              <div className="feat-visual">
                <div className="log-fields">
                  <span>💧</span><span>😴</span><span>🧘</span><span>🥗</span><span>🌸</span><span>🧴</span><span>☁️</span><span>✓</span>
                </div>
              </div>
            </div>

            <div className="feat-card span-3 reveal">
              <div className="feat-label">Glow score</div>
              <h3>Your skin, distilled to a single number.</h3>
              <p>A daily 1–10 you can trust, tracked over time. Nothing fancy. Just the truth.</p>
              <div className="feat-visual">
                <div className="score-card">
                  <div className="score-card-head">
                    <div>
                      <div className="score-label">Avg Glow Score</div>
                      <div className="score-row">
                        <span className="score-num">7.4</span>
                        <span className="score-denom">/10</span>
                        <span className="score-delta">▲ 2.0</span>
                      </div>
                    </div>
                    <div className="score-entries">14 entries</div>
                  </div>
                  <div className="score-tabs">
                    <span>7d</span><span className="on">14d</span><span>30d</span><span>All</span>
                  </div>
                  <svg className="score-chart" viewBox="0 0 320 110" preserveAspectRatio="none">
                    <defs>
                      <linearGradient id="scoreFill" x1="0" x2="0" y1="0" y2="1">
                        <stop offset="0%" stopColor="#E91E8C" stopOpacity=".22" />
                        <stop offset="100%" stopColor="#9B59B6" stopOpacity="0" />
                      </linearGradient>
                      <linearGradient id="scoreLine" x1="0" x2="1">
                        <stop offset="0%" stopColor="#E91E8C" /><stop offset="100%" stopColor="#6C3483" />
                      </linearGradient>
                    </defs>
                    <line x1="0" x2="320" y1="15" y2="15" stroke="rgba(108,52,131,.08)" strokeDasharray="2 3" />
                    <line x1="0" x2="320" y1="55" y2="55" stroke="rgba(108,52,131,.08)" strokeDasharray="2 3" />
                    <line x1="0" x2="320" y1="95" y2="95" stroke="rgba(108,52,131,.08)" strokeDasharray="2 3" />
                    <path d="M 10 38 L 55 70 L 100 88 L 145 30 L 190 60 L 235 18 L 280 45 L 310 15 L 310 105 L 10 105 Z" fill="url(#scoreFill)" />
                    <path d="M 10 38 L 55 70 L 100 88 L 145 30 L 190 60 L 235 18 L 280 45 L 310 15" fill="none" stroke="url(#scoreLine)" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
                    <g fill="#fff" stroke="url(#scoreLine)" strokeWidth="2">
                      <circle cx="10" cy="38" r="3.2" /><circle cx="55" cy="70" r="3.2" /><circle cx="100" cy="88" r="3.2" /><circle cx="145" cy="30" r="3.2" /><circle cx="190" cy="60" r="3.2" /><circle cx="235" cy="18" r="3.6" /><circle cx="280" cy="45" r="3.2" /><circle cx="310" cy="15" r="3.6" />
                    </g>
                  </svg>
                  <div className="score-axis"><span>Mon</span><span>Wed</span><span>Fri</span><span>Sun</span></div>
                </div>
              </div>
            </div>

            <div className="feat-card span-2 reveal">
              <div className="feat-label">Photos</div>
              <h3>Four angles. Same light. Always.</h3>
              <p>Structured, dated, contextualized. Zero chaos.</p>
              <div className="feat-visual">
                <div className="angles">
                  <div className="angle" data-l="Front" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1619451334792-150fd785ee74?w=400&q=80')" }} />
                  <div className="angle" data-l="Left" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1557555187-23d685287bc3?w=400&q=80')" }} />
                  <div className="angle" data-l="Right" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1588387947163-7bfe57b4b1f8?w=400&q=80')" }} />
                  <div className="angle" data-l="Chin" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1614108375897-1d528a2bf37d?w=400&q=80')" }} />
                </div>
              </div>
            </div>

            <div className="feat-card span-2 reveal">
              <div className="feat-label">AI insights</div>
              <h3>Your skin's boosters &amp; drainers.</h3>
              <p>Patterns you'd never see on your own.</p>
              <div className="feat-visual">
                <div className="insights-list">
                  <div className="insight-row"><span className="chip up">+14%</span> 8hr sleep</div>
                  <div className="insight-row"><span className="chip down">-21%</span> High-sugar days</div>
                  <div className="insight-row"><span className="chip up">+9%</span> Niacinamide week</div>
                </div>
              </div>
            </div>

            <div className="feat-card span-2 reveal">
              <div className="feat-label">Routines</div>
              <h3>AM &amp; PM on autopilot.</h3>
              <p>Save your stack, tick it off, correlate to score.</p>
              <div className="feat-visual">
                <div className="routine-row"><span>La Roche SPF 50</span><span className="tick">✓</span></div>
                <div className="routine-row"><span>Vit C serum</span><span className="tick">✓</span></div>
                <div className="routine-row"><span>Retinoid 0.3%</span><span className="empty" /></div>
              </div>
            </div>

            <div className="feat-card span-6 reveal">
              <div className="feat-label">Skin timelapse</div>
              <h3>12 weeks. One video. Undeniable proof.</h3>
              <p style={{ maxWidth: '640px' }}>Frame-by-frame playback from your structured photos. Export it, share it, or just watch the slow-drip glow-up nobody else noticed.</p>
              <div className="feat-visual">
                <div className="timelapse">
                  <span data-d="Wk 1" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1619451334792-150fd785ee74?w=300&q=80')" }} />
                  <span data-d="Wk 3" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1508214751196-bcfd4ca60f91?w=300&q=80')" }} />
                  <span data-d="Wk 5" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1531123897727-8f129e1688ce?w=300&q=80')" }} />
                  <span data-d="Wk 7" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1526510747491-58f928ec870f?w=300&q=80')" }} />
                  <span data-d="Wk 8" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1541823709867-1b206113eafd?w=300&q=80')" }} />
                  <span data-d="Wk 10" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1492106087820-71f1a00d2b11?w=300&q=80')" }} />
                  <span data-d="Wk 11" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1525299374597-911581e1bdef?w=300&q=80')" }} />
                  <span data-d="Wk 12" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1616683693504-3ea7e9ad6fec?w=300&q=80')" }} />
                </div>
              </div>
            </div>

            <div className="feat-card span-6 reveal">
              <div className="feat-label">Analytics</div>
              <h3>Product effectiveness, ranked by correlation.</h3>
              <p style={{ maxWidth: '540px', marginBottom: '20px' }}>Every product in your stack, scored against your best skin days. Impact = avg glow with − without. No vanity metrics.</p>
              <div className="feat-visual">
                <div className="eff-grid">
                  <div className="eff-col">
                    <div className="eff-head eff-head--up">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 17 9 11 13 15 21 7" /><polyline points="14 7 21 7 21 14" /></svg>
                      Glow Boosters
                    </div>
                    <div className="eff-row"><div className="eff-name">Vitamin C Serum<span>3× used · avg 8.3/10</span></div><div className="eff-bar"><i className="up" style={{ width: '78%' }} /></div><div className="eff-val up">+2.3</div></div>
                    <div className="eff-row"><div className="eff-name">Retinol<span>3× used · avg 8.3/10</span></div><div className="eff-bar"><i className="up" style={{ width: '78%' }} /></div><div className="eff-val up">+2.3</div></div>
                    <div className="eff-row"><div className="eff-name">Cleanser<span>7× used · avg 7.0/10</span></div><div className="eff-bar"><i className="up" style={{ width: '34%' }} /></div><div className="eff-val up">+1.0</div></div>
                    <div className="eff-row"><div className="eff-name">SPF 50<span>7× used · avg 7.0/10</span></div><div className="eff-bar"><i className="up" style={{ width: '34%' }} /></div><div className="eff-val up">+1.0</div></div>
                    <div className="eff-row"><div className="eff-name">Niacinamide<span>2× used · avg 7.5/10</span></div><div className="eff-bar"><i className="up" style={{ width: '28%' }} /></div><div className="eff-val up">+0.8</div></div>
                  </div>
                  <div className="eff-col">
                    <div className="eff-head eff-head--down">
                      <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 7 9 13 13 9 21 17" /><polyline points="14 17 21 17 21 10" /></svg>
                      Watch These
                    </div>
                    <div className="eff-row"><div className="eff-name">AHA Exfoliant<span>2× used · avg 4.5/10</span></div><div className="eff-bar"><i className="down" style={{ width: '92%' }} /></div><div className="eff-val down">-3.2</div></div>
                    <div className="eff-row"><div className="eff-name">Heavy moisturizer<span>4× used · avg 5.8/10</span></div><div className="eff-bar"><i className="down" style={{ width: '48%' }} /></div><div className="eff-val down">-1.4</div></div>
                    <div className="eff-row"><div className="eff-name">Fragranced toner<span>3× used · avg 6.2/10</span></div><div className="eff-bar"><i className="down" style={{ width: '30%' }} /></div><div className="eff-val down">-0.9</div></div>
                    <div className="eff-foot">Impact = avg glow with − without · correlation only, not causation</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="block" id="how">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">How it works</span>
            <h2>Four weeks from <span className="display-italic gradient-text">guesswork</span> to answers.</h2>
            <p>No onboarding deck. No 40-question quiz. Just open the app and start.</p>
          </div>
          <div className="steps">
            <div className="step reveal"><div className="step-num">01</div><h3>Log your skin</h3><p>30 seconds a day. Eight fields, a photo, done.</p></div>
            <div className="step reveal"><div className="step-num">02</div><h3>Build the record</h3><p>Two weeks in and you've got more data than any derm appointment.</p></div>
            <div className="step reveal"><div className="step-num">03</div><h3>See the patterns</h3><p>AI surfaces the foods, habits &amp; products moving your score.</p></div>
            <div className="step reveal"><div className="step-num">04</div><h3>Get answers</h3><p>Finally know what's working. Keep it. Cut what isn't.</p></div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="testimonials">
        <div className="container">
          <div className="section-head reveal">
            <span className="eyebrow">Loved by smart skincare nerds</span>
            <h2>Real skin. Real <span className="display-italic gradient-text">receipts</span>.</h2>
            <p>Here's what our early access users are saying — no polish, no paid placement.</p>
          </div>
          <div className="test-grid">
            <div className="test-card reveal">
              <div className="test-stars"><StarSvg /><StarSvg /><StarSvg /><StarSvg /><StarSvg /></div>
              <q>Six weeks in and the AI insights told me dairy was dropping my score by 19%. I didn't believe it. Cut it for a month. My skin's never been clearer. This app is a therapist.</q>
              <div className="test-meta">
                <div className="who-row">
                  <div className="test-avatar" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1531746020798-e6953c6e8e04?w=160&q=80')" }} />
                  <div>
                    <div className="name">Maya R.</div>
                    <div className="who">Radiant Pro · 4 months</div>
                  </div>
                </div>
                <div className="plan">Pro</div>
              </div>
            </div>
            <div className="test-card reveal">
              <div className="test-stars"><StarSvg /><StarSvg /><StarSvg /><StarSvg /><StarSvg /></div>
              <q>I showed my derm the timelapse and trend chart. She literally asked me what app I was using. Got me off one of my prescriptions a cycle early.</q>
              <div className="test-meta">
                <div className="who-row">
                  <div className="test-avatar" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1580489944761-15a19d654956?w=160&q=80')" }} />
                  <div>
                    <div className="name">Priya V.</div>
                    <div className="who">Glow · 7 months</div>
                  </div>
                </div>
                <div className="plan">Glow</div>
              </div>
            </div>
            <div className="test-card reveal">
              <div className="test-stars"><StarSvg /><StarSvg /><StarSvg /><StarSvg /><StarSvg /></div>
              <q>I finally have proof that my skin tanks during luteal phase. Routine tracking showed me I was over-exfoliating week two. Embarrassing how long I did this without data.</q>
              <div className="test-meta">
                <div className="who-row">
                  <div className="test-avatar" style={{ backgroundImage: "url('https://images.unsplash.com/photo-1488426862026-3ee34a7d66df?w=160&q=80')" }} />
                  <div>
                    <div className="name">Jess M.</div>
                    <div className="who">Radiant Pro · 9 months</div>
                  </div>
                </div>
                <div className="plan">Pro</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pricing" id="pricing">
        <div className="container">
          <div className="pricing-head reveal">
            <span className="eyebrow">Pricing</span>
            <h2>Choose your <span className="display-italic gradient-text">plan</span></h2>
            <p>Billed annually · Cancel anytime</p>
          </div>
          <div className="price-grid">
            <div className="price-card reveal">
              <h3>Glow Starter</h3>
              <p className="tagline">Start your skin journey.</p>
              <div className="price-row"><span className="price-num">$0</span></div>
              <p className="price-sub">Free, forever.</p>
              <ul className="features-list">
                <li><CheckSvg />10 log entries / month</li>
                <li><CheckSvg />10 photo uploads total</li>
                <li><CheckSvg />Basic skin tag tracking</li>
                <li><CheckSvg />AM/PM routines (5 products)</li>
                <li><CheckSvg />Glow score tracking</li>
                <li className="off"><CheckSvg />Timeline filters &amp; sorting</li>
                <li className="off"><CheckSvg />Glow analytics &amp; charts</li>
                <li className="off"><CheckSvg />Timelapse creation</li>
                <li className="off"><CheckSvg />Data export</li>
              </ul>
              <Link to="/app" className="btn outlined">Download free</Link>
            </div>

            <div className="price-card popular reveal">
              <div className="price-badge">Most popular</div>
              <h3>Glow</h3>
              <p className="tagline">Unlock your full glow potential.</p>
              <div className="price-row"><span className="price-num">$14.99</span><span className="price-period">/year</span></div>
              <p className="price-sub">That's about $1.25 / month.</p>
              <ul className="features-list">
                <li><CheckSvg />Unlimited log entries</li>
                <li><CheckSvg />100 photo uploads</li>
                <li><CheckSvg />Full timeline filters &amp; sorting</li>
                <li><CheckSvg />Glow analytics &amp; trend charts</li>
                <li><CheckSvg />Timelapse (30-day range)</li>
                <li><CheckSvg />Wishlist (products &amp; treatments)</li>
                <li><CheckSvg />Data export (CSV)</li>
                <li><CheckSvg />Unlimited routine products</li>
                <li className="off"><CheckSvg />AI skin insights</li>
                <li className="off"><CheckSvg />Priority support</li>
              </ul>
              <Link to="/app" className="btn btn-primary">Start free trial</Link>
            </div>

            <div className="price-card best reveal">
              <div className="price-badge alt">Best value</div>
              <h3>Radiant Pro</h3>
              <p className="tagline">Maximum glow, zero compromise.</p>
              <div className="price-row"><span className="price-num">$24.99</span><span className="price-period">/year</span></div>
              <p className="price-sub" style={{ color: 'rgba(255,255,255,.55)' }}>That's about $2.08 / month.</p>
              <ul className="features-list">
                <li><CheckSvg />Everything in Glow</li>
                <li><CheckSvg />Unlimited photos</li>
                <li><CheckSvg />Unlimited timelapse (all-time)</li>
                <li><CheckSvg />AI skin insights &amp; weekly reports</li>
                <li><CheckSvg />Supplement effectiveness scoring</li>
                <li><CheckSvg />Treatment progress history</li>
                <li><CheckSvg />Data export (CSV + JSON)</li>
                <li><CheckSvg />Priority support</li>
              </ul>
              <Link to="/app" className="btn btn-dark">Start free trial</Link>
            </div>
          </div>
          <p className="price-footnote reveal">All paid plans start with a <b>14-day Radiant Pro trial</b>. No credit card required.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq" id="faq">
        <div className="container">
          <div className="section-head reveal" style={{ textAlign: 'center', margin: '0 auto 50px' }}>
            <span className="eyebrow" style={{ marginLeft: '50%', transform: 'translateX(-50%)', display: 'inline-flex' }}>Questions</span>
            <h2 style={{ marginTop: '14px' }}>Still <span className="display-italic gradient-text">wondering</span>?</h2>
          </div>
          <div className="faq-wrap reveal">
            {faqItems.map((item, idx) => (
              <div key={idx} className={`faq-item${openFaq === idx ? ' open' : ''}`}>
                <button className="faq-q" onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}>
                  {item.q}
                  <span className="ico">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M7 2v10M2 7h10" />
                    </svg>
                  </span>
                </button>
                <div className="faq-a"><div><p>{item.a}</p></div></div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="final">
        <div className="container">
          <h2 className="reveal">Stop guessing. Start <span className="display-italic gradient-text">glowing</span>.</h2>
          <p className="reveal">Your skin's been telling you something for years. Glowlytix is how you finally listen.</p>
          <div className="cta-row reveal">
            <AppStoreButton />
            <GooglePlayButton gpId="gpB" />
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="site-footer">
        <div className="container">
          <div className="foot-top">
            <div className="foot-brand">
              <a href="#" className="brand">
                <img className="brand-mark" src="/glowlytix-logo.png" alt="" style={{ filter: 'brightness(1.1)' }} />
                <span className="brand-word">Glowlytix</span>
              </a>
              <p>Your skin, finally in focus.<br />Made with questionable amounts of caffeine in Brooklyn.</p>
            </div>
            <div className="foot-col">
              <h4>Product</h4>
              <ul><li><a href="#features">Features</a></li><li><a href="#pricing">Pricing</a></li><li><a href="#how">How it works</a></li><li><Link to="/app">Download</Link></li></ul>
            </div>
            <div className="foot-col">
              <h4>Company</h4>
              <ul><li><a href="#">About</a></li><li><a href="#">Blog</a></li><li><a href="#">Press</a></li><li><a href="#">Careers</a></li></ul>
            </div>
            <div className="foot-col">
              <h4>Legal</h4>
              <ul><li><Link to="/privacy">Privacy</Link></li><li><Link to="/terms">Terms</Link></li><li><a href="mailto:security@glowlytix.app">Security</a></li></ul>
            </div>
          </div>
          <div className="foot-bottom">
            <span>© 2026 Glowlytix, Inc. All rights reserved.</span>
            <span>Not a medical device. For wellness use only.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
